/**
 * Querverweise im Kurstext ("→ Kapitel 15") in der App klickbar halten.
 *
 * Der Generator setzt fuer BEIDE Frontends dasselbe Ziel, weil der Kursinhalt
 * nur einmal gebaut wird:  <a class="qverweis" href="modul.html?m=m01#b15">
 * Im Web ist das eine echte Seite. In der App gibt es keine modul.html --
 * ein ungefangener Klick landet auf 404.
 *
 * Deshalb hier derselbe Weg, den der KI-Coach fuer seine Sprunglinks nimmt
 * (coach/CoachPanel.tsx): Ziel gegen das strenge Schema pruefen
 * (parseJumpTarget), dann auf /kurs per requestJump springen bzw. von
 * ausserhalb per route() kalt hineinspringen.
 *
 * Drei Dinge, die beim ersten Anlauf gefehlt haben (Cross-Review Codex,
 * 2026-09-03, Befund 1):
 *  - Auf /kurs reicht route() NICHT: die Route bleibt dieselbe, der Screen
 *    zeichnet nicht neu. Dafuer gibt es die Sprung-Bruecke requestJump().
 *  - Ohne stopPropagation() sieht der preact-iso-Router den Klick danach noch
 *    und folgt dem urspruenglichen modul.html-Ziel.
 *  - Ein Klick mit Strg/Cmd oder mit der mittleren Taste wuerde den toten Link
 *    in einem neuen Tab oeffnen. In der App gibt es dieses Ziel nicht, also
 *    wird auch das abgefangen -- lieber intern springen als eine 404 zeigen.
 */
import { requestJump, parseJumpTarget } from "../state/course-nav";
import { activeCourseId } from "../data/course-store";
import { showToast } from "../ui/toast";

const AUF_KURS = () =>
  typeof location !== "undefined" && location.pathname.startsWith("/kurs");

/**
 * Installiert den Abfaenger. Rueckgabe: Funktion zum Abmelden.
 * @param route Navigations-Funktion der App (aus useLocation()).
 */
export function installQuerverweisKlicks(route: (url: string) => void): () => void {
  const behandle = (ev: MouseEvent): void => {
    if (ev.defaultPrevented) return;
    const ziel = ev.target as Element | null;
    const a = ziel?.closest?.("a.qverweis") as HTMLAnchorElement | null;
    if (!a) return;
    // Ab hier gehoert der Klick uns -- auch der mit Zusatztaste, weil das
    // Ziel ausserhalb der App nicht existiert.
    ev.preventDefault();
    ev.stopPropagation();
    const sprung = parseJumpTarget(a.getAttribute("href") || "");
    if (!sprung) {
      // Lieber gar nicht springen als falsch springen.
      showToast("Dieser Verweis führt nicht zu einem Kapitel.");
      return;
    }
    if (AUF_KURS()) {
      requestJump(sprung.m, sprung.b, sprung.u);
      return;
    }
    const kurs = encodeURIComponent(activeCourseId.value);
    const u = sprung.u ? `&u=${encodeURIComponent(sprung.u)}` : "";
    route(`/kurs?course=${kurs}&m=${encodeURIComponent(sprung.m)}${u}#${encodeURIComponent(sprung.b)}`);
  };
  // capture=true: vor dem Router und vor den Vorlese-Gesten auf dem Lesetext.
  document.addEventListener("click", behandle, true);
  document.addEventListener("auxclick", behandle, true);
  return () => {
    document.removeEventListener("click", behandle, true);
    document.removeEventListener("auxclick", behandle, true);
  };
}
